import type {
  User,
  WithAuthenticationRequiredOptions,
} from "@auth0/auth0-react";
import { withAuthenticationRequired as webAuthenticationRequired } from "@auth0/auth0-react";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import * as React from "react";
import { useLocation } from "react-router";
import { LIVE_UPDATE_JUST_RELOADED_KEY } from "../capacitor";
import { captureException, captureMessage } from "../sentry";
import { type ReauthRequest, authFlowController } from "./auth-flow-controller";
import { browserState } from "./browser-state";
import { DefaultOnRedirecting } from "./DefaultOnRedirecting";
import { canUseNative, useAuth0 } from "./use-auth0";

const FATAL_AUTH_CONFIG_ERROR_MARKERS = [
  "service not found",
  "invalid audience",
  "unknown client",
  "application not found",
  "misconfiguration",
];

const isAppBaseRedirectUri = (url: string) => {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === "localhost" &&
      (parsed.pathname === "/" || parsed.pathname === "") &&
      !parsed.search
    );
  } catch {
    return false;
  }
};

const getAuthCallbackError = (url: string) => {
  try {
    const parsed = new URL(url);
    return {
      error: parsed.searchParams.get("error"),
      errorDescription: parsed.searchParams.get("error_description"),
    };
  } catch {
    return {
      error: null,
      errorDescription: null,
    };
  }
};

const isFatalAuthConfigError = (
  error: string | null,
  errorDescription: string | null
) => {
  const lowered = `${error ?? ""} ${errorDescription ?? ""}`.toLowerCase();
  return FATAL_AUTH_CONFIG_ERROR_MARKERS.some((marker) =>
    lowered.includes(marker)
  );
};

const defaultReturnTo = (): string =>
  `${window.location.pathname}${window.location.search}`;

type ExtendedWithAuthenticationRequiredOptions =
  WithAuthenticationRequiredOptions & {
    claimCheck?: (user: User | undefined) => boolean;
  };

const nativeAuthenticationRequired = <P extends object>(
  Component: React.ComponentType<P>,
  {
    returnTo = defaultReturnTo,
    loginOptions = {},
    claimCheck = (): boolean => true,
  }: ExtendedWithAuthenticationRequiredOptions = {}
): React.FC<P> => {
  return function WithAuthenticationRequired(props: P): JSX.Element {
    const {
      handleRedirectCallback,
      isAuthenticated,
      isLoading,
      loginWithRedirect,
      logout,
      user,
    } = useAuth0();

    // We need to have a separate logic during redirection to avoid
    // weird login showing up multiples times bug on android
    const [isRedirecting, setIsRedirecting] = React.useState(false);
    const location = useLocation();

    /**
     * The route is authenticated if the user has valid auth and there are no
     * JWT claim mismatches.
     */
    const rawRouteIsAuthenticated = isAuthenticated && claimCheck(user);

    // Stabilise the authenticated flag: once the user IS authenticated,
    // tolerate brief transient drops of isAuthenticated (caused by Auth0
    // SDK state dispatches in React 17 which are not batched, or by
    // Android WebView lifecycle events).  We keep showing the
    // authenticated tree for a short grace period to avoid unmounting
    // the entire component tree and causing a visible flicker.
    const AUTH_GRACE_MS = 1500;
    const wasAuthenticatedRef = React.useRef(false);
    const authLostAtRef = React.useRef<number | null>(null);

    if (rawRouteIsAuthenticated) {
      wasAuthenticatedRef.current = true;
      authLostAtRef.current = null;
    } else if (wasAuthenticatedRef.current && authLostAtRef.current === null) {
      authLostAtRef.current = Date.now();
    }

    const withinGrace =
      !rawRouteIsAuthenticated &&
      wasAuthenticatedRef.current &&
      authLostAtRef.current !== null &&
      Date.now() - authLostAtRef.current < AUTH_GRACE_MS;

    const routeIsAuthenticated = rawRouteIsAuthenticated || withinGrace;

    // Schedule a re-render at the end of the grace window so we don't
    // stay stuck showing the authenticated tree if auth truly expired.
    const [, forceUpdate] = React.useState(0);
    React.useEffect(() => {
      if (!withinGrace) return;
      const remaining =
        AUTH_GRACE_MS - (Date.now() - (authLostAtRef.current ?? Date.now()));
      const timer = setTimeout(
        () => forceUpdate((n) => n + 1),
        Math.max(remaining, 0)
      );
      return () => clearTimeout(timer);
    }, [withinGrace]);

    // Reset the module-level circuit breaker on successful authentication
    // so that a future logout + re-login flow gets fresh attempts.
    React.useEffect(() => {
      if (routeIsAuthenticated) {
        authFlowController.resetAfterAuthenticated();
      }
    }, [routeIsAuthenticated]);

    const handleLoginWithRedirect = React.useCallback(() => {
      const now = Date.now();
      const decision = authFlowController.decideLoginStart(now);

      // Hard gate: never allow more than MAX_REDIRECT_ATTEMPTS calls total.
      // This duplicates the check in startRedirect but acts as an extra
      // safety net in case the function is called from multiple paths.
      if (!decision.allowed) {
        return;
      }

      authFlowController.markLoginStarted(now);

      const urlParams = new URLSearchParams(location.search);

      void loginWithRedirect({
        ...loginOptions,
        appState: {
          returnTo: typeof returnTo === "function" ? returnTo() : returnTo,
        },
        authorizationParams: {
          login_hint: urlParams.get("login_hint") ?? undefined,
          screen_hint: urlParams.get("screen_hint") ?? undefined,
        },
      }).catch((error) => {
        authFlowController.markLoginFinished();
        setIsRedirecting(false);
        captureException(error, {
          extra: { action: "loginWithRedirect" },
        });
      });
    }, [location.search, loginWithRedirect]);

    const handleReauthRequest = React.useCallback(
      (request: ReauthRequest) => {
        if (!authFlowController.shouldHandleReauthRequest(request)) {
          return;
        }

        // If the login browser is already open the user is actively entering
        // credentials.  Don't mark as handled — a future request (or the
        // auto-redirect effect after browser close) will retry.
        if (browserState.getIsOpen()) {
          return;
        }

        authFlowController.markReauthRequestHandled(request.id);

        // Ensure no stale browser window remains visible before forcing a new login.
        void Browser.close().catch(() => undefined);
        browserState.setOpen(false);

        authFlowController.resetForManualRetry();
        setIsRedirecting(true);
        handleLoginWithRedirect();
      },
      [handleLoginWithRedirect]
    );

    React.useEffect(() => {
      const lastRequest = authFlowController.getLastReauthRequest();
      if (lastRequest) {
        handleReauthRequest(lastRequest);
      }

      return authFlowController.subscribeToReauthRequests(handleReauthRequest);
    }, [handleReauthRequest]);

    // Retry button handler
    const handleRetryClicked = React.useCallback(() => {
      captureMessage("Invoking handleRetryClicked", {
        extra: { isOpenBrowserState: browserState.getIsOpen() },
      });

      // Best-effort close on ALL platforms; ignore errors
      void Browser.close().catch(() => undefined);
      browserState.setOpen(false);

      // Reset the circuit breaker so the user gets fresh attempts
      authFlowController.resetForManualRetry();

      setIsRedirecting(true);
      void handleLoginWithRedirect();
    }, [handleLoginWithRedirect]);

    //auto redirect to login if not authenticated
    React.useEffect(() => {
      if (isLoading || isRedirecting || routeIsAuthenticated) {
        return;
      }

      let cancelled = false;

      const startRedirect = async () => {
        // Circuit breaker: stop auto-redirecting after MAX_REDIRECT_ATTEMPTS
        // consecutive failures. The user can still retry manually via the button.
        // Uses a module-level variable so it survives Auth0Provider remounts.
        if (authFlowController.hasReachedMaxRedirectAttempts()) {
          captureMessage("🔒 Auth redirect circuit breaker triggered", {
            extra: {
              attempts: authFlowController.getCurrentRedirectAttempts(),
            },
          });
          return;
        }

        authFlowController.incrementRedirectAttempts();

        // Determine delay: use a longer delay after a LiveUpdate reload to let
        // the webview and Capacitor plugins stabilise before opening the browser.
        let delay = authFlowController.getNextAutoRedirectDelay(800);
        try {
          const reloadedAt = window.localStorage.getItem(
            LIVE_UPDATE_JUST_RELOADED_KEY
          );
          if (reloadedAt) {
            const elapsed = Date.now() - Number(reloadedAt);
            if (elapsed < 30_000) {
              delay = Math.max(delay, 2500);
            }
            window.localStorage.removeItem(LIVE_UPDATE_JUST_RELOADED_KEY);
          }
        } catch {
          // localStorage unavailable; use default delay
        }

        await new Promise((resolve) => setTimeout(resolve, delay));

        if (!cancelled) {
          if (browserState.getIsOpen()) {
            return;
          }

          authFlowController.clearNextAutoRedirectDelay();
          setIsRedirecting(true);
          handleLoginWithRedirect();
        }
      };

      void startRedirect();

      return () => {
        cancelled = true;
      };
    }, [
      isLoading,
      isRedirecting,
      routeIsAuthenticated,
      handleLoginWithRedirect,
    ]);

    React.useEffect(() => {
      const handler = ({ url }: { url: string }) => {
        void (async () => {
          let wasRealCallback = false;
          try {
            if (url.includes("/callback/logout")) return;

            const hasAuthParams =
              url.includes("state") &&
              (url.includes("code") || url.includes("error"));

            if (hasAuthParams) {
              if (!authFlowController.startAuthCallback(url)) {
                return;
              }

              wasRealCallback = true;
              if (url.includes("error")) {
                console.error("🔒 Auth redirect error", url);
                const { error, errorDescription } = getAuthCallbackError(url);
                const fatalConfigError = isFatalAuthConfigError(
                  error,
                  errorDescription
                );

                captureMessage("🔒 Auth redirect callback error", {
                  extra: {
                    error,
                    errorDescription,
                    fatalConfigError,
                    url,
                  },
                });

                if (fatalConfigError) {
                  // Avoid calling Auth0 logout for tenant/app misconfiguration
                  // errors; logout itself opens the browser and creates loops.
                  authFlowController.markFatalConfigError();

                  captureMessage(
                    "🔒 Fatal Auth0 config error detected; auto-redirect disabled",
                    {
                      extra: {
                        error,
                        errorDescription,
                        url,
                      },
                      level: "warning",
                    }
                  );
                } else {
                  await logout();
                }
              } else {
                try {
                  await handleRedirectCallback(url);
                } catch (err) {
                  console.error("🔒 handleRedirectCallback error", err);
                  captureException(err, {
                    extra: { action: "handleRedirectCallback" },
                  });
                }
              }

              // Best-effort close only on real auth callbacks.
              // Closing on non-auth appUrlOpen events can prematurely dismiss
              // the browser and re-trigger the login flow.
              void Browser.close().catch(() => undefined);
              browserState.setOpen(false);
            } else {
              // Non-auth callback (for example iOS returning APP_ID://localhost
              // after closing the browser without completing auth).
              browserState.setOpen(false);

              // Reset in-flight login tracking so the next auto attempt is allowed.
              authFlowController.resetInFlightLogin();

              // Retry automatically with exponential backoff on browser-return
              // callbacks, but keep strict circuit breakers to avoid login loops.
              if (isAppBaseRedirectUri(url)) {
                // On iOS, this callback can arrive while SFSafariViewController
                // is still visible with a blank page. Close it explicitly.
                void Browser.close().catch(() => undefined);

                const { backoffDelay, nonAuthRetryAttempts } =
                  authFlowController.scheduleNonAuthRetry();

                captureMessage("🔒 Non-auth callback auto-retry scheduled", {
                  extra: {
                    backoffDelay,
                    nonAuthRetryAttempts,
                    redirectAttempts:
                      authFlowController.getCurrentRedirectAttempts(),
                  },
                });
              } else {
                authFlowController.clearNextAutoRedirectDelay();
              }

              // Re-enable auto redirect effect; delay and max attempts are guarded.
              setIsRedirecting(false);
            }
          } finally {
            if (wasRealCallback) {
              authFlowController.finishAuthCallback();
              setIsRedirecting(false);
            }
          }
        })();
      };

      let cancelled = false;
      let listenerHandle: Awaited<ReturnType<typeof App.addListener>> | null =
        null;

      void App.addListener("appUrlOpen", handler).then((handle) => {
        if (cancelled) {
          void handle.remove();
        } else {
          listenerHandle = handle;
        }
      });

      return () => {
        cancelled = true;
        if (listenerHandle) {
          void listenerHandle.remove();
        }
      };
    }, [handleRedirectCallback, logout]);

    if (isRedirecting) {
      return (
        <DefaultOnRedirecting
          onRetryClick={handleRetryClicked}
          reason={routeIsAuthenticated ? "sessionExpired" : undefined}
        />
      );
    }

    return routeIsAuthenticated ? (
      <Component {...props} />
    ) : (
      <DefaultOnRedirecting onRetryClick={handleRetryClicked} />
    );
  };
};

const webAuthenticationRequiredWithReauth = <P extends object>(
  Component: React.ComponentType<P>,
  {
    returnTo = defaultReturnTo,
    loginOptions = {},
    ...options
  }: ExtendedWithAuthenticationRequiredOptions = {}
): React.FC<P> => {
  const ReauthAwareComponent = (props: P): JSX.Element => {
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    const [isRedirecting, setIsRedirecting] = React.useState(false);
    const location = useLocation();

    React.useEffect(() => {
      if (isAuthenticated && !isRedirecting) {
        authFlowController.resetAfterAuthenticated();
      }
    }, [isAuthenticated, isRedirecting]);

    const handleLoginWithRedirect = React.useCallback(() => {
      const now = Date.now();
      const decision = authFlowController.decideLoginStart(now);

      if (!decision.allowed) {
        return;
      }

      authFlowController.markLoginStarted(now);

      const urlParams = new URLSearchParams(location.search);

      void loginWithRedirect({
        ...loginOptions,
        appState: {
          returnTo: typeof returnTo === "function" ? returnTo() : returnTo,
        },
        authorizationParams: {
          login_hint: urlParams.get("login_hint") ?? undefined,
          screen_hint: urlParams.get("screen_hint") ?? undefined,
        },
      }).catch((error) => {
        authFlowController.markLoginFinished();
        setIsRedirecting(false);
        captureException(error, {
          extra: { action: "webLoginWithRedirect" },
        });
      });
    }, [location.search, loginWithRedirect]);

    const handleReauthRequest = React.useCallback(
      (request: ReauthRequest) => {
        if (!authFlowController.shouldHandleReauthRequest(request)) {
          return;
        }

        authFlowController.markReauthRequestHandled(request.id);
        authFlowController.resetForManualRetry();
        setIsRedirecting(true);
        handleLoginWithRedirect();
      },
      [handleLoginWithRedirect]
    );

    React.useEffect(() => {
      const lastRequest = authFlowController.getLastReauthRequest();
      if (lastRequest) {
        handleReauthRequest(lastRequest);
      }

      return authFlowController.subscribeToReauthRequests(handleReauthRequest);
    }, [handleReauthRequest]);

    const handleRetryClicked = React.useCallback(() => {
      captureMessage("Invoking web auth retry", {
        extra: { isAuthenticated },
      });

      authFlowController.resetForManualRetry();
      setIsRedirecting(true);
      handleLoginWithRedirect();
    }, [handleLoginWithRedirect, isAuthenticated]);

    if (isRedirecting) {
      return (
        <DefaultOnRedirecting
          onRetryClick={handleRetryClicked}
          reason="sessionExpired"
        />
      );
    }

    return <Component {...props} />;
  };

  return webAuthenticationRequired(ReauthAwareComponent, {
    ...options,
    loginOptions,
    returnTo,
  });
};

const withAuthenticationRequired = <P extends object>(
  Component: React.ComponentType<P>,
  options: ExtendedWithAuthenticationRequiredOptions = {}
): React.FC<P> => {
  if (canUseNative() === false) {
    return webAuthenticationRequiredWithReauth(Component, options);
  }
  return nativeAuthenticationRequired(Component, options);
};

export { withAuthenticationRequired };
